import { StatusItemDto, getStatusItemsBySpreadsheet } from "../../services/status-items.service";
import { getAuditLog } from "../../services/audit-log.service";
import { ExcelService } from "../../services/excel.service";
import { isStandaloneMode } from "../../utils/standalone-mode";
import { SpreadsheetReviewInfoResponse } from "../../services/spreadsheets.service";

/**
 * Builds and writes the Excel completion certificate. Shared between
 * ManagerTab (manager sign-off preview) and PartnerTab (terminal state).
 *
 * The underlying {@link ExcelService.createCompletionCertificate} clears
 * and rewrites the "Completion Certificate" tab on every call, so it's
 * safe to invoke on both manager and partner sign-off — the tab starts
 * with just the Manager block after a manager sign, then gets replaced
 * with the full Manager + Partner cert once the partner signs.
 *
 * Standalone: skipped. The certificate is written into the currently-open
 * workbook, which doesn't exist in standalone. The backend sign-off flip
 * and stakeholder emails run independently in the caller, so this being a
 * no-op doesn't affect the sign-off itself.
 *
 * Never throws — sign-off has already succeeded server-side by the time
 * this runs, and a certificate problem must not make a completed sign-off
 * look failed. Instead it RETURNS a user-facing message when something
 * went wrong (null on success), so the caller can surface it. Callers
 * should show that message: a read-only workbook can cause Excel to fork
 * the document into a separate copy, and without a message the user has
 * no way to tell where their certificate went.
 */
export const tryCreateCompletionCertificate = async (
  info: SpreadsheetReviewInfoResponse,
  spreadsheetId: string | null,
  statusItemsFallback: StatusItemDto[],
): Promise<string | null> => {
  if (isStandaloneMode()) return null;

  const managerSigned = !!(info.signingManagerEmail && info.signingManagerTime);
  const partnerSigned = !!(info.signingPartnerEmail && info.signingPartnerTime);
  // Someone MUST be signed — the excel-service impl also guards this but
  // bailing here saves a round-trip fetch for status items / audit log.
  if (!managerSigned && !partnerSigned) return null;

  try {
    let certificateStatusItems: {
      name: string;
      status: string;
      lastUpdated: string;
      updatedBy: string;
    }[] = [];

    if (spreadsheetId) {
      try {
        const [freshStatusItems, auditEntries] = await Promise.all([
          getStatusItemsBySpreadsheet(spreadsheetId),
          getAuditLog(spreadsheetId, { limit: 1000 }),
        ]);

        const latestByEntity = new Map<string, { userName: string; createdAt: string }>();
        for (const entry of auditEntries) {
          if (entry.entityType === "status_item" && entry.entityId) {
            if (!latestByEntity.has(entry.entityId)) {
              latestByEntity.set(entry.entityId, {
                userName: entry.userName,
                createdAt: entry.createdAt,
              });
            }
          }
        }

        certificateStatusItems = freshStatusItems
          .filter((si) => !si.isHidden)
          .map((si) => {
            const auditInfo = latestByEntity.get(si.id.toString());
            return {
              name: si.name,
              status: si.status || "Open",
              lastUpdated: auditInfo?.createdAt || si.lastUpdated,
              updatedBy: auditInfo?.userName || "",
            };
          });
      } catch (auditErr) {
        console.error("Failed to fetch data for certificate:", auditErr);
        certificateStatusItems = statusItemsFallback
          .filter((si) => !si.isHidden)
          .map((si) => ({
            name: si.name,
            status: si.status || "Open",
            lastUpdated: si.lastUpdated,
            updatedBy: "",
          }));
      }
    }

    // Build review points list from both snapshots (titles only, partner takes precedence)
    const reviewPoints: { title: string; status: string; snapshot: string }[] = [];
    if (info.partnerReviewPointsSnapshot) {
      for (const rp of info.partnerReviewPointsSnapshot.reviewPoints) {
        reviewPoints.push({ title: rp.title, status: rp.status, snapshot: "Partner Sign-off" });
      }
    }
    if (info.managerReviewPointsSnapshot) {
      for (const rp of info.managerReviewPointsSnapshot.reviewPoints) {
        if (!reviewPoints.some((r) => r.title === rp.title)) {
          reviewPoints.push({ title: rp.title, status: rp.status, snapshot: "Manager Sign-off" });
        }
      }
    }

    await ExcelService.createCompletionCertificate({
      managerEmail: info.signingManagerEmail ?? undefined,
      managerSignedTime: info.signingManagerTime ?? undefined,
      partnerEmail: info.signingPartnerEmail ?? undefined,
      partnerSignedTime: info.signingPartnerTime ?? undefined,
      secondaryPartnerEmail: info.secondarySigningPartnerEmail ?? undefined,
      secondaryPartnerSignedTime: info.secondarySigningPartnerTime ?? undefined,
      managerChecklist: info.managerChecklistResponses?.items.map((item) => ({
        text: item.text,
        response: item.response,
      })),
      partnerChecklist: info.partnerChecklistResponses?.items.map((item) => ({
        text: item.text,
        response: item.response,
      })),
      statusItems: certificateStatusItems,
      reviewPoints,
    });
    // Persist the certificate sheet so it isn't lost if the user closes
    // without saving. A save failure is reported separately from a write
    // failure — the certificate IS in the in-memory workbook at this
    // point, so the user just needs to save manually.
    try {
      await ExcelService.save();
    } catch (saveErr) {
      console.error("Certificate written but workbook save failed:", saveErr);
      return (
        "The completion certificate was added to this workbook, but saving failed. " +
        "Press Ctrl+S to save it yourself."
      );
    }
    return null;
  } catch (err) {
    console.error("Failed to create completion certificate:", err);
    // Sign-off itself already succeeded server-side, so we never rethrow
    // — but we DO hand the caller a message to show. Silently swallowing
    // this is how a read-only workbook turned into a mystery bug report:
    // Excel forked the document into a copy, the certificate landed
    // there, and nothing anywhere told the user what had happened.
    return err instanceof Error && err.message
      ? err.message
      : "The sign-off was recorded, but the completion certificate could not be written to this workbook.";
  }
};
